import Loading from '@Components/General/Feedback/Loading';
import Api from '@Contexts/Api';
import axiosInstance from '@api/axiosInstance';
import { OrgMemberInfo, useAuthInfo } from '@propelauth/react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useLocalStorage } from '@uidotdev/usehooks';
import { useEffect, useRef, useState } from 'react';

const ApiProvider = ({
    router,
    children,
}: {
    router: ReturnType<typeof useRouter>;
    children: React.ReactNode;
}) => {
    const { accessToken, orgHelper } = useAuthInfo();
    const orgs = orgHelper?.getOrgs();
    const queryClient = useQueryClient();
    const firstOrgId = orgs?.[0].orgId;

    const [lastSelectedOrgId, setLastSelectedOrgId] = useLocalStorage(
        'selectedOrgId',
        firstOrgId,
    );

    const activeOrg = orgHelper?.getOrg(lastSelectedOrgId);

    if (!activeOrg) setLastSelectedOrgId(firstOrgId);

    const [loading, setLoading] = useState(true);

    const setActiveOrg = (orgId: string) => setLastSelectedOrgId(orgId);

    useEffect(() => {
        if (!accessToken || !activeOrg) return;
        const reqInterceptor = axiosInstance.interceptors.request.use(
            config => {
                config.headers.Authorization = `Bearer ${accessToken}`;
                config.params = {
                    ...(config.params || {}),
                    org_id: activeOrg.orgId,
                };
                return config;
            },
            error => {
                return Promise.reject(error);
            },
        );

        setLoading(false);

        return () => {
            axiosInstance.interceptors.request.eject(reqInterceptor);
        };
    }, [accessToken, activeOrg]);

    const rendered = useRef(false);

    useEffect(() => {
        if (rendered.current) {
            queryClient.resetQueries();
            router.clearCache();
        } else {
            rendered.current = true;
        }
    }, [lastSelectedOrgId]);

    // if (loading) return <h1>Loading API...</h1>;
    if (loading) return <Loading />;

    return (
        <Api.Provider value={{ activeOrg, setActiveOrg }}>
            {children}
        </Api.Provider>
    );
};

export default ApiProvider;
